import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { debounce } from 'lodash';
import { useQueryClient } from '@tanstack/react-query';

import { TagContextProvider } from 'lib/context/TagContext/TagContextProvider';

import ErrorBoundary from 'components/shared/Error/Boundary';
import TagsReorderWrapper from 'components/shared/Tags/components/Reorder/TagsReorderWrapper';

import { updateTagLinks } from '../../lib/updateTagLinks';

import TagTableView from './components/TagTableView';

/**
 * @param {import('./lib/propTypes').TagsCellProps} props
 */

const TagsCell = (props) => {
  const { value, data, query, refTable, kind, label, fromTable = false } = props;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isCreateModal, setIsCreateModal] = useState(false);
  const [openReorderModal, setOpenReorderModal] = useState(false);

  const debouncedUpdateTags = debounce(async (id, tags, mode) => {
    await updateTagLinks({
      navigate,
      queryClient,
      id,
      tags,
      mode,
      query,
      refTable,
      fromTable
    });
  }, 1000);

  const handleAddTags = (data, tag) => {
    const filteredNewTags = tag?.filter(
      (newTag) => !data?.taglinks?.some((existingTag) => existingTag?.id === newTag?.id)
    );
    debouncedUpdateTags(data?.id, filteredNewTags, 'add');
  };

  const handleRemoveTag = async (data, id) => {
    await updateTagLinks({
      navigate,
      queryClient,
      id: data?.id,
      fromTable,
      tags: [id],
      mode: 'remove',
      query,
      refTable
    });
  };

  const tableActions = [
    {
      label: 'Create tag',
      icon: 'tag-new-mono',
      onClick: () => setIsCreateModal(true)
    },
    {
      label: 'Reorder tags',
      icon: 'arrow-swap-down-mono',
      onClick: () => setOpenReorderModal(true),
      disabled: value?.length < 2,
      tippyText:
        value?.length < 2 ? `Select tags to add to this ${label || kind}` : 'Reorder selected tags'
    },
    {
      label: 'Manage tags',
      icon: 'settings-mono',
      onClick: () => navigate('/portal/settings/tags'),
      disabled: false
    }
  ];

  return (
    <ErrorBoundary>
      <TagContextProvider>
        <TagTableView
          currTags={value || []}
          tagsKind={kind}
          handleAdd={(tag) => handleAddTags(data, tag)}
          handleRemove={(id) => handleRemoveTag(data, id)}
          tableActions={tableActions}
          isCreateModal={isCreateModal}
          setIsCreateModal={setIsCreateModal}
          usePendingState
          fromTable
          removeConfirmationTitle={`Are you sure you want to remove this tag from the ${label || kind}?`}
        />
        {openReorderModal && (
          <TagsReorderWrapper
            openReorderModal={openReorderModal}
            handleClose={() => setOpenReorderModal(false)}
            refId={data?.id}
            refTable={refTable}
            fromTable
            kind={kind}
          />
        )}
      </TagContextProvider>
    </ErrorBoundary>
  );
};

export default TagsCell;
